import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/api'

export type AdminRole = 'super_admin' | 'admin' | 'moderator'

export interface User {
  id: number
  username: string
  email: string
  phone?: string
  admin_role: AdminRole
  image?: string
  totp_enabled?: boolean
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(localStorage.getItem('admin_token'))
  const isInitialized = ref(false)

  // 2FA state
  const pendingTempToken = sessionStorage.getItem('admin_2fa_temp_token')
  const _requires2FA = ref(Boolean(pendingTempToken) && !token.value)
  const _requires2FASetup = ref(false)
  const _tempToken = ref<string | null>(pendingTempToken)

  const isAuthenticated = computed(() => !!token.value)
  const requires2FA = computed(() => _requires2FA.value)
  const requires2FASetup = computed(() => _requires2FASetup.value)
  const isSuperAdmin = computed(() => user.value?.admin_role === 'super_admin')
  const isAdmin = computed(() => ['super_admin', 'admin'].includes(user.value?.admin_role || ''))

  async function login(email: string, password: string) {
    const response = await api.post('/user/login', { email, password })
    const data = response.data
    
    const adminRoles = ['super_admin', 'admin', 'moderator']

    // Challenge не содержит постоянного JWT. Обрабатываем его до проверки
    // обычного success-контракта, иначе password+2FA вход всегда отклоняется.
    if (data.requires2FA) {
      const challengeRole = data.adminRole || data.role
      if (!challengeRole || !adminRoles.includes(challengeRole)) {
        throw new Error('У вас нет доступа к админ-панели')
      }
      _requires2FA.value = true
      _tempToken.value = data.tempToken
      sessionStorage.setItem('admin_2fa_temp_token', data.tempToken)
      return
    }

    const normalizedUser = data.user || data
    const role = normalizedUser.admin_role || normalizedUser.role || data.role
    const accessToken = data.accessToken || data.token
    if (!role || !adminRoles.includes(role) || !accessToken) {
      throw new Error('У вас нет доступа к админ-панели')
    }
    
    token.value = accessToken
    user.value = { ...normalizedUser, admin_role: role }
    _requires2FASetup.value = data.requires2FASetup ?? normalizedUser.totp_enabled !== true
    localStorage.setItem('admin_token', accessToken)
    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
  }

  async function loginWithFirebase(firebaseToken: string) {
    const deviceToken = localStorage.getItem('admin_device_token')
    const response = await api.post('/user/firebase-admin', {
      token: firebaseToken,
      deviceToken
    })
    const data = response.data

    if (data.requires2FA) {
      const challengeRole = data.adminRole || data.role
      if (challengeRole && !['super_admin', 'admin', 'moderator'].includes(challengeRole)) {
        throw new Error('У вас нет доступа к админ-панели')
      }
      _requires2FA.value = true
      _tempToken.value = data.tempToken
      sessionStorage.setItem('admin_2fa_temp_token', data.tempToken)
      return
    }

    // Успешный логин
    token.value = data.accessToken
    user.value = data.user
    localStorage.setItem('admin_token', data.accessToken)
    api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`

    // Проверяем нужна ли настройка 2FA
    _requires2FASetup.value = data.requires2FASetup ?? data.user?.totp_enabled !== true
  }

  function replaceToken(accessToken: string) {
    token.value = accessToken
    localStorage.setItem('admin_token', accessToken)
    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
  }

  async function setup2FA(currentCode?: string): Promise<{ qrCode: string; secret: string }> {
    const response = await api.post('/admin/2fa/setup', currentCode ? { current_code: currentCode } : {})
    return response.data
  }

  async function confirm2FA(code: string): Promise<{ backupCodes: string[] }> {
    const response = await api.post('/admin/2fa/confirm', { code })
    if (response.data.accessToken) replaceToken(response.data.accessToken)
    if (response.data.user) user.value = response.data.user
    _requires2FASetup.value = false
    return response.data
  }

  async function verify2FA(code: string, trustDevice: boolean) {
    const response = await api.post('/admin/2fa/verify', {
      tempToken: _tempToken.value,
      code,
      trustDevice,
      userAgent: navigator.userAgent
    })
    const data = response.data

    _requires2FA.value = false
    _tempToken.value = null
    sessionStorage.removeItem('admin_2fa_temp_token')

    token.value = data.accessToken
    user.value = data.user
    localStorage.setItem('admin_token', data.accessToken)
    api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`

    if (data.deviceToken) {
      localStorage.setItem('admin_device_token', data.deviceToken)
    }

    _requires2FASetup.value = data.requires2FASetup ?? data.user?.totp_enabled !== true
  }

  async function verifyEmailCode(code: string, trustDevice: boolean) {
    const response = await api.post('/admin/2fa/verify-email', {
      tempToken: _tempToken.value,
      code,
      trustDevice,
      userAgent: navigator.userAgent
    })
    const data = response.data

    _requires2FA.value = false
    _tempToken.value = null
    sessionStorage.removeItem('admin_2fa_temp_token')

    token.value = data.accessToken
    user.value = data.user
    localStorage.setItem('admin_token', data.accessToken)
    api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`

    if (data.deviceToken) {
      localStorage.setItem('admin_device_token', data.deviceToken)
    }

    _requires2FASetup.value = data.requires2FASetup ?? data.user?.totp_enabled !== true
  }

  async function sendEmailCode() {
    await api.post('/admin/2fa/send-email-code', { tempToken: _tempToken.value })
  }

  async function get2FAStatus(): Promise<{ enabled: boolean; backupCodesRemaining: number }> {
    const response = await api.get('/admin/2fa/status')
    return response.data
  }

  async function fetchProfile() {
    if (!token.value) return

    api.defaults.headers.common['Authorization'] = `Bearer ${token.value}`

    let response
    try {
      response = await api.get('/user/profile')
    } catch (error) {
      // Разлогиниваем только при реальном отказе авторизации. Сетевые/CORS/5xx
      // ошибки (response отсутствует) означают временную недоступность сервера —
      // токен валиден, чистить его и выкидывать на /login нельзя.
      const status = (error as { response?: { status?: number } }).response?.status
      if (status === 401 || status === 403) {
        logout()
      }
      throw error
    }

    const userData = response.data
    const adminRoles = ['super_admin', 'admin', 'moderator']
    if (!userData.admin_role || !adminRoles.includes(userData.admin_role)) {
      logout()
      throw new Error('У вас нет доступа к админ-панели')
    }

    user.value = userData
    _requires2FASetup.value = userData.totp_enabled !== true
  }

  function logout() {
    user.value = null
    token.value = null
    _requires2FA.value = false
    _requires2FASetup.value = false
    _tempToken.value = null
    sessionStorage.removeItem('admin_2fa_temp_token')
    localStorage.removeItem('admin_token')
    delete api.defaults.headers.common['Authorization']
  }

  // Initialize on load
  async function initialize() {
    if (token.value) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token.value}`
      try {
        await fetchProfile()
      } catch (e) {
        // Token invalid
      }
    }
    isInitialized.value = true
  }

  return {
    user,
    token,
    isAuthenticated,
    isInitialized,
    isSuperAdmin,
    isAdmin,
    requires2FA,
    requires2FASetup,
    login,
    loginWithFirebase,
    logout,
    fetchProfile,
    initialize,
    setup2FA,
    confirm2FA,
    verify2FA,
    verifyEmailCode,
    sendEmailCode,
    get2FAStatus,
    replaceToken
  }
})
